import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { productApprovalsApi } from '../data/productApprovalsApi';
import { useGetApproversQuery } from '../../__generated__/chirp/com/hubspot/product/approvals/rpc/ApprovalsConfigurationRpc.hooks';
import { useAppHubUsers } from './useAppHubUsers';
import { useGetIsUngated } from './useGetIsUngated';
import { GATES } from '../constants/auth';
import { captureException } from '../utils/sentryUtils';
import { actorToApprovalUser } from '../utils/chirpApprovalAdapters';
export const useApprovers = (approvalType, approvalAction) => {
  const getIsUngated = useGetIsUngated();
  const isChirpEnabled = getIsUngated(GATES.CHIRP_MIGRATION);
  const isUnmounted = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const fetchApprovers = useCallback(() => {
    if (isChirpEnabled) return;
    setIsLoading(true);
    productApprovalsApi.fetchApprovers(approvalType, approvalAction).then(response => {
      if (isUnmounted.current === true) {
        return;
      }
      setApprovers(response);
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  }, [approvalType, approvalAction, isChirpEnabled]);
  useEffect(() => {
    fetchApprovers();
    return () => {
      isUnmounted.current = true;
    };
  }, [fetchApprovers]);
  const {
    data: chirpData,
    loading: chirpLoading
  } = useGetApproversQuery({
    variables: {
      approvalType,
      approvalAction
    },
    skip: !isChirpEnabled,
    onError: error => {
      captureException(error, {
        extra: {
          fingerprint: 'useApprovers-chirp',
          approvalType,
          approvalAction
        }
      });
    }
  });
  const actorUserIds = useMemo(() => {
    if (!(chirpData !== null && chirpData !== void 0 && chirpData.approvers)) return [];
    return chirpData.approvers.map(actor => Number(actor.id));
  }, [chirpData]);
  const {
    usersRecord,
    loading: usersLoading
  } = useAppHubUsers(isChirpEnabled ? actorUserIds : []);
  const chirpApprovers = useMemo(() => {
    if (!(chirpData !== null && chirpData !== void 0 && chirpData.approvers) || usersLoading) return [];
    return chirpData.approvers.map(actor => actorToApprovalUser(actor.id, usersRecord, 'useApprovers'));
  }, [chirpData, usersRecord, usersLoading]);
  if (isChirpEnabled) {
    return {
      approvers: chirpApprovers,
      isLoading: chirpLoading || usersLoading
    };
  }
  return {
    approvers,
    isLoading
  };
};